/** FINANCES — l'indicateur central n'est pas le chiffre d'affaires,
 *  c'est ce que ton heure de travail te rapporte vraiment. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Coins } from "lucide-react";
import { api, messageErreur } from "../api";
import { useApp } from "../App";
import { Barre, Erreur, Micro, Squelette, Vide } from "../components/Bits";
import { euros, jourCourt, nb } from "../lib/format";

const PERIODES = [
  { c: "semaine", l: "7 jours" },
  { c: "mois", l: "30 jours" },
  { c: "trimestre", l: "90 jours" },
  { c: "annee", l: "1 an" },
];

/* Une phrase d'abord. Les chiffres ensuite. */
function phraseFinances(d) {
  const r = d.resume;
  if (!r.volume)
    return "Aucune prestation encaissée sur la période. Dès que tu clôtures des rendez-vous, tout se calcule ici.";
  const bouts = [
    `${euros(r.ca)} pour ${nb(r.heures_travaillees)} h de travail, soit ${euros(r.euro_heure)} de l'heure.`,
  ];
  if (d.evolution_pct !== null && d.evolution_pct !== undefined) {
    bouts.push(
      d.evolution_pct >= 0
        ? `En hausse de ${nb(Math.abs(d.evolution_pct))} % sur la période précédente.`
        : `En baisse de ${nb(Math.abs(d.evolution_pct))} % sur la période précédente.`
    );
  }
  if (r.cout_absences > 0) bouts.push(`${euros(r.cout_absences)} perdus en absences.`);
  return bouts.join(" ");
}

function Chiffre({ micro, valeur, note, testid }) {
  return (
    <div data-testid={testid}>
      <Micro muted>{micro}</Micro>
      <div className="num text-[24px] md:text-[28px] leading-none mt-2">{valeur}</div>
      {note && (
        <div className="text-[12px] mt-1.5" style={{ color: "var(--text-subtle)" }}>
          {note}
        </div>
      )}
    </div>
  );
}

export default function Finances() {
  const { compte, version } = useApp();
  const [periode, setPeriode] = useState("mois");
  const [data, setData] = useState(null);
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setCharge(true);
    setErreur(null);
    try {
      const { data } = await api.get("/finances", { params: { periode } });
      setData(data);
    } catch (e) {
      setErreur(messageErreur(e, "Les finances ne sont pas disponibles."));
    } finally {
      setCharge(false);
    }
  }, [periode]);

  useEffect(() => {
    charger();
  }, [charger, version]);

  const maxCa = useMemo(
    () => (data ? Math.max(...data.serie.map((s) => s.ca), 1) : 1),
    [data]
  );
  const maxHeure = useMemo(
    () => (data ? Math.max(...data.prestations.map((p) => p.euro_heure), 1) : 1),
    [data]
  );

  if (!compte?.plan?.finances)
    return (
      <div className="pt-4">
        <Vide
          icone={Coins}
          titre="Les finances font partie du plan complet"
          texte="Servae calcule ce que rapporte réellement chaque prestation à l'heure travaillée, ton taux de remplissage et le coût exact de tes absences."
        />
      </div>
    );

  return (
    <div className="pb-8" data-testid="page-finances">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Micro>Finances</Micro>
          <h1 className="display text-[26px] md:text-[32px] mt-1.5" data-testid="titre-finances">
            Ce que vaut ton temps
          </h1>
        </div>
        <div
          className="flex p-1 gap-1"
          style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-full)" }}
        >
          {PERIODES.map((p) => {
            const on = periode === p.c;
            return (
              <button
                key={p.c}
                onClick={() => setPeriode(p.c)}
                data-testid={`periode-${p.c}`}
                className="h-9 px-3.5 text-[13px] font-semibold cible-tactile"
                style={{
                  borderRadius: "var(--radius-full)",
                  background: on ? "var(--surface)" : "transparent",
                  color: on ? "var(--text)" : "var(--text-subtle)",
                  boxShadow: on ? "var(--shadow-sm)" : "none",
                  transition: "background-color var(--dur) var(--ease), color var(--dur) var(--ease)",
                }}
              >
                {p.l}
              </button>
            );
          })}
        </div>
      </div>

      {charge ? (
        <div className="pt-6">
          <Squelette lignes={3} />
        </div>
      ) : erreur ? (
        <div className="pt-6">
          <Erreur message={erreur} surReessai={charger} />
        </div>
      ) : (
        <>
          <p
            className="text-[17px] md:text-[19px] leading-[1.5] mt-5 max-w-[48ch] pose"
            data-testid="phrase-finances"
          >
            {phraseFinances(data)}
          </p>

          {/* --------------------------------------------- le chiffre roi */}
          <div
            className="mt-8 p-6 md:p-7 pose"
            data-testid="bloc-euro-heure"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-sm)",
            }}
          >
            <Micro>Ton heure de travail</Micro>
            <div className="flex items-end gap-3 mt-2">
              <span className="num text-[46px] md:text-[58px] leading-none">
                {euros(data.resume.euro_heure)}
              </span>
              <span className="text-[14px] pb-2" style={{ color: "var(--text-muted)" }}>
                / heure
              </span>
            </div>
            <p className="text-[13.5px] mt-3" style={{ color: "var(--text-muted)" }}>
              Temps de remise en état compris. C'est le seul chiffre qui dit si tes tarifs
              tiennent la route.
            </p>
          </div>

          <div
            className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-6 mt-8 pt-7"
            style={{ borderTop: "1px solid var(--border)" }}
          >
            <Chiffre
              micro="Encaissé"
              valeur={euros(data.resume.encaisse)}
              note={
                data.resume.en_attente > 0
                  ? `${euros(data.resume.en_attente)} encore à encaisser`
                  : "Tout est rentré"
              }
              testid="stat-encaisse"
            />
            <Chiffre
              micro="Prestations"
              valeur={data.resume.volume}
              note={`Panier moyen ${euros(data.resume.panier_moyen)}`}
              testid="stat-volume"
            />
            <Chiffre
              micro="Heures travaillées"
              valeur={`${nb(data.resume.heures_travaillees)} h`}
              note={`Remplissage ${nb(data.remplissage.taux)} %`}
              testid="stat-heures"
            />
            <Chiffre
              micro="Absences"
              valeur={data.resume.absences}
              note={
                data.resume.cout_absences > 0
                  ? `${euros(data.resume.cout_absences)} envolés`
                  : "Aucune, bravo"
              }
              testid="stat-absences"
            />
          </div>

          <div className="grid lg:grid-cols-[1fr_360px] gap-10 mt-10">
            {/* ------------------------------------- rentabilité par presta */}
            <section className="min-w-0">
              <div
                className="flex items-baseline justify-between pb-2.5 mb-1"
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                <Micro>Ce que chaque prestation te rapporte à l'heure</Micro>
              </div>
              {data.prestations.length === 0 ? (
                <p className="text-[14px] py-5" style={{ color: "var(--text-muted)" }}>
                  Rien d'encaissé sur la période.
                </p>
              ) : (
                <div>
                  {data.prestations.map((p, i) => {
                    const meilleur = i === 0;
                    const pire = i === data.prestations.length - 1 && data.prestations.length > 1;
                    return (
                      <div
                        key={p.service_id}
                        className="py-4"
                        style={{ borderBottom: "1px solid var(--border)" }}
                        data-testid={`presta-rentabilite-${p.service_id}`}
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="text-[15px] font-semibold truncate">{p.nom}</span>
                          <span className="num text-[16px] shrink-0">
                            {euros(p.euro_heure)}
                            <span className="text-[12px]" style={{ color: "var(--text-subtle)" }}>
                              {" "}
                              / h
                            </span>
                          </span>
                        </div>
                        <div className="mt-2.5">
                          <Barre
                            pct={(p.euro_heure / maxHeure) * 100}
                            ton={meilleur ? "olive" : pire ? "ambre" : "accent"}
                          />
                        </div>
                        <div
                          className="flex items-center justify-between gap-3 mt-2 text-[12.5px]"
                          style={{ color: "var(--text-muted)" }}
                        >
                          <span>
                            <span className="num">{p.volume}</span> fois ·{" "}
                            <span className="num">{nb(p.heures)}</span> h ·{" "}
                            <span className="num">{euros(p.ca)}</span>
                          </span>
                          <span style={{ color: "var(--text-subtle)" }}>
                            {meilleur
                              ? "Ta prestation la plus rentable"
                              : pire
                              ? "Celle qui te fait travailler pour le moins"
                              : ""}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  {data.prestations.length > 1 && (
                    <p className="text-[13px] mt-4" style={{ color: "var(--text-muted)" }}>
                      Entre {data.prestations[0].nom} et{" "}
                      {data.prestations[data.prestations.length - 1].nom}, il y a{" "}
                      <span className="num">
                        {euros(
                          data.prestations[0].euro_heure -
                            data.prestations[data.prestations.length - 1].euro_heure
                        )}
                      </span>{" "}
                      d'écart par heure travaillée. C'est là qu'il y a un tarif à revoir.
                    </p>
                  )}
                </div>
              )}
            </section>

            {/* ----------------------------------------------- la courbe */}
            <aside className="space-y-6">
              <div>
                <div
                  className="flex items-baseline justify-between pb-2.5 mb-4"
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <Micro>Jour par jour</Micro>
                  <span className="num text-[12px]" style={{ color: "var(--text-subtle)" }}>
                    {euros(maxCa)} max
                  </span>
                </div>
                <div className="flex items-end gap-[3px]" style={{ height: 120 }}>
                  {data.serie.map((s) => (
                    <span
                      key={s.date}
                      title={`${jourCourt(s.date)} · ${euros(s.ca)}`}
                      style={{
                        flex: 1,
                        height: `${Math.max(2, (s.ca / maxCa) * 100)}%`,
                        background: s.ca > 0 ? "var(--accent)" : "var(--surface-sunken)",
                        borderRadius: 3,
                      }}
                    />
                  ))}
                </div>
                <div
                  className="flex justify-between mt-2 text-[11px]"
                  style={{ color: "var(--text-subtle)" }}
                >
                  <span>{jourCourt(data.serie[0]?.date)}</span>
                  <span>{jourCourt(data.serie[data.serie.length - 1]?.date)}</span>
                </div>
              </div>

              <div
                className="p-5"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-lg)",
                }}
              >
                <Micro>Remplissage</Micro>
                <div className="flex items-baseline justify-between mt-2 mb-2.5">
                  <span className="num text-[26px] leading-none">
                    {nb(data.remplissage.taux)} %
                  </span>
                </div>
                <Barre pct={data.remplissage.taux} />
                <p className="text-[12.5px] mt-3" style={{ color: "var(--text-muted)" }}>
                  {data.remplissage.taux >= 75
                    ? "Ton agenda est plein : c'est le moment d'augmenter tes tarifs, pas tes horaires."
                    : data.remplissage.taux >= 50
                    ? "Il te reste de la place. Une relance bien placée la remplit."
                    : "Beaucoup de créneaux vides. Poste ton lien, et relance ceux qui ne sont pas revenus."}
                </p>
              </div>

              {Object.keys(data.resume.par_moyen || {}).length > 0 && (
                <div>
                  <Micro className="pb-2.5 mb-3 block" style={{ borderBottom: "1px solid var(--border)" }}>
                    Moyens de paiement
                  </Micro>
                  {Object.entries(data.resume.par_moyen).map(([m, v]) => (
                    <div key={m} className="flex items-baseline justify-between py-1.5">
                      <span className="text-[14px] capitalize">{m}</span>
                      <span className="num text-[14px]">{euros(v)}</span>
                    </div>
                  ))}
                </div>
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
